/* eslint-disable no-magic-numbers */
import { useQuery } from '@tanstack/react-query'
import { Flex } from 'antd'

import { ComponentLibrary } from '@suntex.classic/adobe-animate'
import { ArrayUtil, GraphicsUtil, Sprite, UIUtil } from '@suntex.core/animation'
import { $Color, Color, ColorInput } from '@suntex.core/color'
import { Entities_Player_ProgressSummary_Response } from '@suntex.fim/api-client'
import { useFramework } from '@suntex.fim/framework'
import { usePlayerContext, usePlayerProgressSummary } from '@suntex.fim/player'

import Canvas from '../Shared/Canvas'

export function AchievementsPage() {
  const { api } = useFramework()
  const { playerId, playerInfo } = usePlayerContext()
  const progressSummary = usePlayerProgressSummary()

  const { data: achievements } = useQuery({
    queryKey: ['player', 'achievements', playerId],
    queryFn: () => api.get_Player_Achievements({ playerId }),
    enabled: playerId > 0
  })

  const hubInfo = achievements?.playerHubInfo
  const earnedToday = hubInfo
    ? hubInfo.stickers - hubInfo.stickers1D
    : undefined
  const earnedThisWeek = hubInfo
    ? hubInfo.stickers - hubInfo.stickers1W
    : undefined
  const earnedLastWeek = hubInfo
    ? hubInfo.stickers1W - hubInfo.stickers2W
    : undefined

  return (
    <Flex vertical align="center" gap={20}>
      <section>
        <header>
          <h3>Skill Level</h3>
        </header>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'auto auto',
            gap: '4px 12px',
            justifyContent: 'end',
            fontSize: 14,
            marginBottom: 8
          }}
        >
          <span>Stickers earned today:</span>
          <strong>{earnedToday?.toLocaleString() ?? '–'}</strong>
          <span>Stickers earned this week:</span>
          <strong>{earnedThisWeek?.toLocaleString() ?? '–'}</strong>
          <span>Stickers earned last week:</span>
          <strong>{earnedLastWeek?.toLocaleString() ?? '–'}</strong>
        </div>
        <Canvas
          {...{
            name: 'SkillMeter',
            compositionId: 'E8AB864D5D5BDF4FA61C303F0A4E8715',
            width: 800,
            height: 350,
            jsSrc: 'SkillMeter.js',
            parameters: {
              type: 1,
              exp: playerInfo?.player.stickers
            }
          }}
        />
      </section>

      <section>
        <header>
          <h3>Awards</h3>
        </header>
        <Canvas
          {...{
            name: 'Achievements',
            compositionId: '4A510FD90302C54C8F68B0B17C85A7F2',
            width: 800,
            height: 800,
            jsSrc: 'Achievements.js',
            ProgramClass: getProgramClass(progressSummary),
            parameters: {
              playerId
            }
          }}
        />
      </section>
    </Flex>
  )
}

interface AchievementsExportRoot extends createjs.DisplayObject {
  mainPanel: createjs.Container & {
    mainTile: Tile
  }
}

type Tile = createjs.Container & {
  icon: createjs.DisplayObject
  whiteText: createjs.Text
  blackText: createjs.Text
  earned: boolean
  text: string
  iconFrame: number
  color: Color
  linkText: string
  linkHref: string
  colorSprite: Sprite
}

function getProgramClass(
  playerProgressSummary: Partial<Entities_Player_ProgressSummary_Response>
) {
  return Achievements_prog

  function Achievements_prog(
    _stage: createjs.Stage,
    _exportRoot: createjs.DisplayObject,
    lib: ComponentLibrary
  ) {
    const exportRoot = _exportRoot as AchievementsExportRoot
    const mainPanel = exportRoot.mainPanel

    function run() {
      Achievements()
    }

    const TEST = false

    const links = [
      null,
      ['Play Skill Sets', '/player/#/ss'],
      ['Play Just the Facts', '/player/#/jtf'],
      ['Play Practice Gyms', '/player/#/gyms'],
      ['Play Practice Gyms', '/player/#/gyms'],
      ['Play Know & Show', '/player/#/ks'],
      ['Play Measurement World', '/player/#/mw'],
      ['Play Bonus Games', '/player/#/bonus']
    ] as const

    const specialLinks = [
      ['Play Just the Facts', '/player/#/jtf'],
      ['Play Practice Gyms', '/player/#/gyms'],
      ['Play Know & Show', '/player/#/ks'],
      ['Play Measurement World', '/player/#/mw'],
      ['Play Bonus Games', '/player/#/bonus'],
      ['Play Skill Sets', '/player/#/ss']
    ] as const

    const texts = [
      [
        '\n\nEarn 1,000\nstickers',
        '\n\nEarn 2,000\nstickers',
        '\n\nEarn 3,000\nstickers',
        '\n\nEarn 4,000\nstickers',
        '\n\nEarn 5,000\nstickers',
        '\n\nEarn 6,000\nstickers',
        '\n\nEarn 8,000\nstickers',
        '\n\nEarn 10,000\nstickers'
      ],
      [
        '\n\nComplete\nSkill Set 1',
        '\n\nComplete\nSkill Set 2',
        '\n\nComplete\nSkill Set 3',
        '\n\nComplete\nSkill Set 4',
        '\n\nComplete\nSkill Set 5',
        '\n\nComplete\nSkill Set 6',
        '\n\nComplete\nSkill Set 7',
        '\n\nComplete\nSkill Set 8'
      ],
      [
        '\nComplete all\npre-tests in\nJTF 10x10\nWhole Numbers',
        '\nComplete all\npre-tests in\nJTF 10x10\nFractions',
        '\nComplete all\npre-tests in\nJTF 10x10\nDecimals',
        '\nComplete all\npre-tests in\nJTF 10x10\nIntegers',
        '\nComplete all\npost-tests in\nJTF 10x10\nWhole Numbers',
        '\nComplete all\npost-tests in\nJTF 10x10\nFractions',
        '\nComplete all\npost-tests in\nJTF 10x100\nDecimals',
        '\nComplete all\npost-tests in\nJTF 10x10\nIntegers'
      ],
      [
        '\nComplete the\nWhole Number\nAddition\nGym',
        '\nComplete the\nWhole Number\nSubtraction\nGym',
        '\nComplete the\nWhole Number\nMultiplication\nGym',
        '\nComplete the\nWhole Number\nDivision\nGym',
        '\nComplete the\nFraction\nAddition\nGym',
        '\nComplete the\nFraction\nSubtraction\nGym',
        '\nComplete the\nFraction\nMultiplication\nGym',
        '\nComplete the\nFraction\nDivision\nGym'
      ],
      [
        '\nComplete the\nDecimal\nAddition\nGym',
        '\nComplete the\nDecimal\nSubtraction\nGym',
        '\nComplete the\nDecimal\nMultiplication\nGym',
        '\nComplete the\nDecimal\nDivision\nGym',
        '\nComplete the\nInteger\nAddition\nGym',
        '\nComplete the\nInteger\nSubtraction\nGym',
        '\nComplete the\nInteger\nMultiplication\nGym',
        '\nComplete the\nInteger\nDivision\nGym'
      ],
      [
        '\nEarn 3\nsilver stars in\nKnow & Show',
        '\nEarn 5\nsilver stars in\nKnow & Show',
        '\nEarn 7\nsilver stars in\nKnow & Show',
        '\nEarn 9\nsilver stars in\nKnow & Show',
        '\nEarn 3\ngold stars in\nKnow & Show',
        '\nEarn 5\ngold stars in\nKnow & Show',
        '\nEarn 7\ngold stars in\nKnow & Show',
        '\nEarn 9\ngold stars in\nKnow & Show'
      ],
      [
        '\nEarn 1 \ngold star in\nMeasurement\nWorld',
        '\nEarn 2 \ngold stars in\nMeasurement\nWorld',
        '\nEarn 3 \ngold stars in\nMeasurement\nWorld',
        '\nEarn 4 \ngold stars in\nMeasurement\nWorld',
        '\nEarn 5 \ngold stars in\nMeasurement\nWorld',
        '\nEarn 6 \ngold stars in\nMeasurement\nWorld',
        '\nEarn 7 \ngold stars in\nMeasurement\nWorld',
        '\nEarn 8 \ngold stars in\nMeasurement\nWorld'
      ],
      [
        '\nEarn 1 \ngold star in\nBonus games',
        '\nEarn 2 \ngold stars in\nBonus games',
        '\nEarn 3 \ngold stars in\nBonus games',
        '\nEarn 4 \ngold stars in\nBonus games',
        '\nEarn 5 \ngold stars in\nBonus games',
        '\nEarn 6 \ngold stars in\nBonus games',
        '\nEarn 7 \ngold stars in\nBonus games',
        '\nEarn 8 \ngold stars in\nBonus games'
      ]
    ] as const

    const colors = [
      [
        0x5533aa, 0x663399, 0x773388, 0x883377, 0x993366, 0xaa3355, 0xbb3344,
        0xcc3333
      ],
      [
        0xff9933, 0xffcc33, 0x66cc33, 0x339966, 0x3399cc, 0x3366ff, 0x6633cc,
        0xcc33ff
      ],
      [
        0x6666cc, 0xcc6666, 0x6699aa, 0x8844cc, 0x3333cc, 0xcc0000, 0x0099aa,
        0x7700cc
      ],
      [
        0x3355dd, 0x3366cc, 0x3377bb, 0x3388aa, 0xcc3333, 0xdd4433, 0xee5533,
        0xff6633
      ],
      [
        0x22aaaa, 0x22aa88, 0x22aa66, 0x22aa44, 0x8800cc, 0x9900bb, 0xaa00aa,
        0xbb0099
      ],
      [
        0x8877cc, 0x7766dd, 0x6655ee, 0x5544ff, 0x00cc88, 0x00bb99, 0x00aaaa,
        0x0099bb
      ],
      [
        0xffbb33, 0xffaa33, 0xee8833, 0xee7733, 0xdd6633, 0xdd5533, 0xcc4433,
        0xcc3333
      ],
      [
        0xaa3377, 0x993388, 0x884499, 0x7744aa, 0x6655bb, 0x5555cc, 0x4466dd,
        0x3366ee
      ]
    ] as const

    const specialTexts = [
      '\n\nComplete all of\nJust the Facts',
      '\n\nComplete all\nPractice Gyms',
      '\n\nComplete all of\nKnow and Show',
      '\n\nComplete any 10\nMeasurement\nWorld games',
      '\n\nComplete any 10\nBonus games',
      '\n\nComplete all 8\nSkill Sets'
    ] as const

    const specialColors = [
      0xffdd22, 0xd71155, 0x2399bd, 0xff8800, 0x660099, 0xff3300
    ] as const

    const gymTags = ['num', 'frc', 'dec', 'int'] as const
    const jtfTags = ['num', 'frc', 'dec', 'int'] as const
    //var gymTags = ["num","frc","dec","int","k2"];
    const opTags = ['add', 'sub', 'mul', 'div'] as const
    const ksTags = ['1', '2', '3', '4', '5', '6', '8'] as const

    function Achievements() {
      mainPanel.mainTile.icon.alpha = 0.3
      GraphicsUtil.shadow(mainPanel.mainTile.whiteText)

      const jtfComplete = getJTFComplete()
      const ksCount = getKSCount()
      let gymsComplete = true
      let ssComplete = true

      let iTile

      for (let i = 0; i < 8; i++) {
        for (let ii = 0; ii < 8; ii++) {
          iTile = new lib['AchievementTile']() as Tile
          initTile(
            iTile,
            texts[i][ii],
            i * 8 + ii + 1,
            colors[i][ii],
            links[i]!
          )
          iTile.x = 200 + (72 + 3) * ii
          iTile.y = 5 + (72 + 4) * i

          switch (i) {
            case 0:
              if (
                getDatum('stickers') >=
                (ii + 1) * 1000 + (ii > 5 ? (ii - 5) * 1000 : 0)
              )
                earnTile(iTile)
              break
            case 1:
              if (getDatum(['ss', ii + 1, 3, 'star']) > 0) earnTile(iTile)
              else ssComplete = false
              break
            case 2:
              if (jtfComplete[ii]) earnTile(iTile)
              break
            case 3:
              if (getGymComplete(gymTags[Math.floor(ii / 4)], (ii % 4) + 1))
                earnTile(iTile)
              else gymsComplete = false
              break
            case 4:
              if (getGymComplete(gymTags[Math.floor(ii / 4) + 2], (ii % 4) + 1))
                earnTile(iTile)
              else gymsComplete = false
              break
            case 5:
              if (ksCount[Math.floor(ii / 4)] > (ii % 4) * 2 + 2)
                earnTile(iTile)
              break
            case 6:
              if (getDatum('mwGoldCount') > ii) earnTile(iTile)
              break
            case 7:
              if (getDatum('bonusGoldCount') > ii) earnTile(iTile)
              break
          }
          mainPanel.addChild(iTile)
        }
      }

      for (let i = 0; i < 6; i++) {
        iTile = new lib['AchievementTile']() as Tile
        initTile(
          iTile,
          specialTexts[i],
          57 + i,
          specialColors[i],
          specialLinks[i]
        )

        iTile.scaleY = iTile.scaleX = 1.7
        iTile.x = 4 + 134 * i
        iTile.y = 655
        mainPanel.addChild(iTile)

        switch (i) {
          case 0:
            if (jtfComplete[8]) earnTile(iTile)
            break
          case 1:
            if (gymsComplete) earnTile(iTile)
            break
          case 2:
            if (ksCount[1] >= 53) earnTile(iTile)
            break
          case 3:
            if (getDatum('mwGoldCount') >= 10) earnTile(iTile)
            break
          case 4:
            if (getDatum('bonusGoldCount') >= 10) earnTile(iTile)
            break
          case 5:
            if (ssComplete) earnTile(iTile)
            break
        }
      }
    }

    function initTile(
      tile: Tile,
      text: string,
      iconFrame: number,
      color: ColorInput,
      link?: readonly [string, string]
    ) {
      tile.text = text
      tile.iconFrame = iconFrame
      tile.color = $Color.from(color)
      if (link) {
        tile.linkText = link[0]
        tile.linkHref = link[1]
      }

      UIUtil.onClick(tile, onClick)
      UIUtil.onMouseOver(tile, onFocus)

      UIUtil.delay(updateFrame, 50, [tile, iconFrame, 0xeeeeee, 0xdddddd])
      tile.blackText.text = text
    }

    function updateFrame(tile, iconFrame, color, frameColor) {
      colorTile(tile, color, frameColor)
      UIUtil.setFrame(tile.icon, iconFrame)
    }

    function onClick(e) {
      onFocus(e)
    }

    function onFocus(e) {
      const tile = UIUtil.getEventTarget<Tile>(e)

      if (tile.earned) {
        mainPanel.mainTile.whiteText.text = tile.text
        mainPanel.mainTile.blackText.text = ''
        colorTile(mainPanel.mainTile, tile.color, $Color.from(0x666666))
      } else {
        mainPanel.mainTile.whiteText.text = ''
        mainPanel.mainTile.blackText.text = tile.text
        colorTile(
          mainPanel.mainTile,
          $Color.from(0xeeeeee),
          $Color.from(0x666666)
        )
      }
      UIUtil.setFrame(mainPanel.mainTile.icon, tile.iconFrame)
      //UIUtil.delay(UIUtil.setFrame,50,[mainPanel.mainTile.icon, tile.iconFrame]);
    }

    function earnTile(tile) {
      tile.earned = true

      //colorTile(tile, tile.color, 0x666666);
      //UIUtil.setFrame(tile.icon, tile.iconFrame);
      UIUtil.delay(updateFrame, 50, [
        tile,
        tile.iconFrame,
        tile.color,
        0x666666
      ])
      //tile.icon.alpha = .8;
      tile.blackText.visible = false
    }

    function colorTile(tile: Tile, color: Color, frameColor: Color) {
      if (tile.colorSprite) {
        tile.removeChildAt(0)
      }

      const colorSprite = GraphicsUtil.createSprite()
      GraphicsUtil.beginFill(colorSprite, frameColor)
      GraphicsUtil.drawRect(colorSprite, 0, 0, 72, 72)
      GraphicsUtil.beginFill(colorSprite, color)
      GraphicsUtil.drawRect(colorSprite, 2, 2, 68, 68)

      tile.addChildAt(colorSprite, 0)
      tile.colorSprite = colorSprite
    }

    function getJTFComplete() {
      const jtfComplete = [true, true, true, true, true, true, true, true, true]

      for (let i = 0; i < 4; i++) {
        for (let ii = 0; ii < 4; ii++) {
          const val = getDatum(['jtf', '10x10', jtfTags[i], opTags[ii]]) //parseInt(vars["jtf_"+jtfTags[i]+"_"+(ii+1)]);
          if (val < 2) {
            jtfComplete[i + 4] = false
            jtfComplete[8] = false
            if (val < 1) {
              jtfComplete[i] = false
              jtfComplete[8] = false
            }
          }
        }
      }

      return jtfComplete
    }

    function getGymComplete(tag, op) {
      for (let i = 1; i <= 12; i++) {
        const val = getDatum(['gyms', tag, opTags[op - 1], i]) //parseInt(vars["gym_"+tag+"_"+op+"_"+i]);
        if (val < 2) {
          return false
        }
      }
      return true
    }

    function getKSCount() {
      let silverCount = 0
      let goldCount = 0
      let maxScore
      let numCategories
      for (let i = 0; i < ksTags.length; i++) {
        if (i < 2) {
          numCategories = 4
          maxScore = 24
        } else {
          numCategories = 9
          maxScore = 48
        }
        for (let ii = 1; ii <= numCategories; ii++) {
          const val = getDatum(['ks', ksTags[i], ii])
          if (val >= maxScore) {
            goldCount++
            silverCount++
          } else if (val >= maxScore / 2) {
            silverCount++
          }
        }
      }
      return [silverCount, goldCount]
    }

    function getDatum(keys: string | (string | number)[]): number {
      const val = ArrayUtil.getProperty(playerProgressSummary, keys)
      return val as number
    }

    return { run }
  }
}
